import React from "react";
import { observer } from "mobx-react";
import { EvaluationQueryCreateStore } from "../../store";
import { EvaluationQueryCreateForm } from "./EvaluationQueryCreateForm";
import { ErrorDialog } from "../../../../common/controls";
import { SavingOverlay } from "../../../../common/controls/forms/SavingOverlay";
import { EvaluationPathType } from "../../../../api/graphql";
import { EvaluationDifficulty } from "../../../../api/graphql";
import { EvaluationAmbiguityType } from "../../../../api/graphql";

export type EvaluationQueryCreateProps = {
  evaluationQueryCreateStore: EvaluationQueryCreateStore;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
};

export const EvaluationQueryCreate: React.FC<EvaluationQueryCreateProps> =
  observer(
    ({ evaluationQueryCreateStore, showToolbox = true, showSaveFooter }) => {
      return (
        <>
          <SavingOverlay open={evaluationQueryCreateStore.isSaving} />
          <EvaluationQueryCreateForm
            evaluationQueryCreateStore={evaluationQueryCreateStore}
            queryError={evaluationQueryCreateStore.errors.query?.message}
            onQueryChange={(value: string) => {
              evaluationQueryCreateStore.setQueryValue(value);
            }}
            query={evaluationQueryCreateStore.entityValues.query}
            pathTypeError={evaluationQueryCreateStore.errors.pathType?.message}
            pathTypeOptions={evaluationQueryCreateStore.getPathTypeOptions()}
            onPathTypeChange={(value: EvaluationPathType) => {
              evaluationQueryCreateStore.setPathTypeValue(value);
            }}
            pathType={evaluationQueryCreateStore.entityValues.pathType}
            difficultyError={
              evaluationQueryCreateStore.errors.difficulty?.message
            }
            difficultyOptions={evaluationQueryCreateStore.getDifficultyOptions()}
            onDifficultyChange={(value: EvaluationDifficulty) => {
              evaluationQueryCreateStore.setDifficultyValue(value);
            }}
            difficulty={evaluationQueryCreateStore.entityValues.difficulty}
            expectedToolError={
              evaluationQueryCreateStore.errors.expectedTool?.message
            }
            onExpectedToolChange={(value: string) => {
              evaluationQueryCreateStore.setExpectedToolValue(value);
            }}
            expectedTool={evaluationQueryCreateStore.entityValues.expectedTool}
            expectedArgsError={
              evaluationQueryCreateStore.errors.expectedArgs?.message
            }
            onExpectedArgsChange={(value: string) => {
              evaluationQueryCreateStore.setExpectedArgsValue(value);
            }}
            expectedArgs={evaluationQueryCreateStore.entityValues.expectedArgs}
            acceptableToolsError={
              evaluationQueryCreateStore.errors.acceptableTools?.message
            }
            onAcceptableToolsChange={(value: string) => {
              evaluationQueryCreateStore.setAcceptableToolsValue(value);
            }}
            acceptableTools={
              evaluationQueryCreateStore.entityValues.acceptableTools
            }
            expectedResponsePatternError={
              evaluationQueryCreateStore.errors.expectedResponsePattern?.message
            }
            onExpectedResponsePatternChange={(value: string) => {
              evaluationQueryCreateStore.setExpectedResponsePatternValue(value);
            }}
            expectedResponsePattern={
              evaluationQueryCreateStore.entityValues.expectedResponsePattern
            }
            generatedFromError={
              evaluationQueryCreateStore.errors.generatedFrom?.message
            }
            onGeneratedFromChange={(value: string) => {
              evaluationQueryCreateStore.setGeneratedFromValue(value);
            }}
            generatedFrom={
              evaluationQueryCreateStore.entityValues.generatedFrom
            }
            isAmbiguousError={
              evaluationQueryCreateStore.errors.isAmbiguous?.message
            }
            onIsAmbiguousChange={(value: boolean) => {
              evaluationQueryCreateStore.setIsAmbiguousValue(value);
            }}
            isAmbiguous={evaluationQueryCreateStore.entityValues.isAmbiguous}
            ambiguityTypeError={
              evaluationQueryCreateStore.errors.ambiguityType?.message
            }
            ambiguityTypeOptions={evaluationQueryCreateStore.getAmbiguityTypeOptions()}
            onAmbiguityTypeChange={(value: EvaluationAmbiguityType | "") => {
              evaluationQueryCreateStore.setAmbiguityTypeValue(value);
            }}
            ambiguityType={
              evaluationQueryCreateStore.entityValues.ambiguityType
            }
            disambiguationResponseError={
              evaluationQueryCreateStore.errors.disambiguationResponse?.message
            }
            onDisambiguationResponseChange={(value: string) => {
              evaluationQueryCreateStore.setDisambiguationResponseValue(value);
            }}
            disambiguationResponse={
              evaluationQueryCreateStore.entityValues.disambiguationResponse
            }
            notesError={evaluationQueryCreateStore.errors.notes?.message}
            onNotesChange={(value: string) => {
              evaluationQueryCreateStore.setNotesValue(value);
            }}
            notes={evaluationQueryCreateStore.entityValues.notes}
            showToolbox={showToolbox}
            showSaveFooter={showSaveFooter}
            saving={evaluationQueryCreateStore.isProcessing}
            onSave={() => evaluationQueryCreateStore.saveNewEvaluationQuery()}
            onCancel={() =>
              evaluationQueryCreateStore.cancelNewEvaluationQuery()
            }
          />
          <ErrorDialog
            open={evaluationQueryCreateStore.errorDialogOpened}
            title={evaluationQueryCreateStore.errorTitle}
            description={evaluationQueryCreateStore.errorDescription}
            onClose={() => evaluationQueryCreateStore.closeErrorDialog()}
          />
        </>
      );
    }
  );
